import { useEffect } from "react";

/**
 * Lightweight SEO helper — updates <title>, meta description, canonical,
 * Open Graph / Twitter tags, and injects JSON-LD structured data.
 * No dependency on react-helmet-async.
 */
function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => {
      if (k !== "content") el.setAttribute(k, v);
    });
    document.head.appendChild(el);
  }
  if (attrs.content !== undefined) el.setAttribute("content", attrs.content);
  return el;
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.text = JSON.stringify(data);
  return el;
}

export default function SEO({
  title,
  description,
  keywords,
  path = "/",
  image,
  type = "website",
  jsonLd,
  ogTitle,
  ogDescription,
  ogImage,
  ogType,
  twitterCard,
  twitterTitle,
  twitterDescription,
  twitterImage,
  robots,
  canonicalPath,
}) {
  useEffect(() => {
    const siteName = "InstaVivah";
    const alreadyBranded =
      title && title.toLowerCase().includes(siteName.toLowerCase());
    const fullTitle = title
      ? alreadyBranded
        ? title
        : `${title} | ${siteName}`
      : `${siteName} — Free Matrimonial Biodata Maker & Marriage Profiles`;
    document.title = fullTitle;

    const origin =
      typeof window !== "undefined" ? window.location.origin : "";
    const url = `${origin}${canonicalPath || path}`;
    const fallbackImg =
      "https://images.pexels.com/photos/35069916/pexels-photo-35069916.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
    const finalOgImage = ogImage || image || fallbackImg;
    const finalTwImage = twitterImage || image || finalOgImage;

    upsertMeta('meta[name="description"]', {
      name: "description",
      content: description || "",
    });
    if (keywords) {
      upsertMeta('meta[name="keywords"]', {
        name: "keywords",
        content: keywords,
      });
    }
    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: robots || "index, follow",
    });

    upsertLink("canonical", url);

    // Open Graph
    upsertMeta('meta[property="og:title"]', {
      property: "og:title",
      content: ogTitle || fullTitle,
    });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: ogDescription || description || "",
    });
    upsertMeta('meta[property="og:type"]', {
      property: "og:type",
      content: ogType || type,
    });
    upsertMeta('meta[property="og:url"]', {
      property: "og:url",
      content: url,
    });
    upsertMeta('meta[property="og:image"]', {
      property: "og:image",
      content: finalOgImage,
    });
    upsertMeta('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: siteName,
    });

    // Twitter
    upsertMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: twitterCard || "summary_large_image",
    });
    upsertMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: twitterTitle || ogTitle || fullTitle,
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: twitterDescription || ogDescription || description || "",
    });
    upsertMeta('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: finalTwImage,
    });

    if (jsonLd) {
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      arr.forEach((d, i) => upsertJsonLd(`ld-page-${i}`, d));
    }

    return () => {
      // Clean up per-page JSON-LD when navigating away
      if (jsonLd) {
        const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
        arr.forEach((_, i) => {
          const el = document.getElementById(`ld-page-${i}`);
          if (el) el.remove();
        });
      }
    };
  }, [
    title, description, keywords, path, image, type, jsonLd,
    ogTitle, ogDescription, ogImage, ogType,
    twitterCard, twitterTitle, twitterDescription, twitterImage,
    robots, canonicalPath,
  ]);

  return null;
}
